import { declaredProfile } from './artworkControlConventions';

// Scoped viewer access to reviewed artwork controls. The source itself is
// unchanged; an appended closure reads only the reviewed script's local state.
const nativeControl = target => target && target.closest
  && target.closest('input, textarea, select, [contenteditable="true"], [data-artwork-controls], [data-pillar-controls]');

export const pillarQuestion = value => String(value).toUpperCase()
  .replace(/[^ A-Z0-9.,?!';:-]/g, '').slice(0, 60);

export default function artworkInput(p, sketch, profile) {
  const listeners = [];
  const scopedWindow = new Proxy(window, {
    get(target, name) {
      if (name === 'addEventListener') return (type, listener, options) => {
        const wrapped = event => {
          if (!nativeControl(event.target)) listener.call(target, event);
        };
        listeners.push({ type, listener, wrapped, options });
        target.addEventListener(type, wrapped, options);
      };
      if (name === 'removeEventListener') return (type, listener, options) => {
        const entry = listeners.find(l => l.type === type && l.listener === listener);
        target.removeEventListener(type, entry ? entry.wrapped : listener, options);
      };
      const value = target[name];
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
  const readState = sketch(p, scopedWindow);

  // Some projects install their keys when props arrive, after construction.
  // Setters keep native fields protected even when a handler is replaced.
  for (const name of ['keyPressed', 'keyTyped', 'keyReleased', 'mousePressed', 'mouseReleased', 'mouseClicked']) {
    let wrapped;
    const set = handler => {
      wrapped = typeof handler === 'function'
        ? event => nativeControl(event && event.target) ? true : handler.call(p, event)
        : handler;
    };
    set(p[name]);
    Object.defineProperty(p, name, { configurable: true, get: () => wrapped, set });
  }

  function key(value) {
    const codes = { Escape: 27, Backspace: 8, Enter: 13, ArrowLeft: 37, ArrowUp: 38, ArrowRight: 39, ArrowDown: 40 };
    let stopped = false;
    const event = {
      type: 'keydown', key: value, keyCode: codes[value] || value.toUpperCase().charCodeAt(0),
      preventDefault() {}, stopImmediatePropagation() { stopped = true; },
    };
    for (const entry of listeners) {
      if (entry.type === 'keydown') entry.listener.call(window, event);
      if (stopped) break;
    }
    if (stopped) return;
    const previous = { key: p.key, keyCode: p.keyCode };
    p.key = value; p.keyCode = event.keyCode;
    try {
      if (p.keyPressed) p.keyPressed(event);
      if (value.length === 1 && p.keyTyped) p.keyTyped(event);
      if (p.keyReleased) p.keyReleased({ ...event, type: 'keyup' });
    } finally { p.key = previous.key; p.keyCode = previous.keyCode; }
  }

  function close() {
    if (profile.interaction === 'pillars') {
      for (let i = 0; i < 4; i++) key('Escape');
    }
  }

  const controls = {
    profile() {
      if (profile.source === 'reviewed') return profile;
      // An invalid explicit declaration opts out instead of exposing guessed keys.
      if (p.immutablesControls !== undefined) return declaredProfile(p.immutablesControls);
      return profile;
    },
    snapshot() {
      const current = controls.profile();
      if (!current) return { ready: false, mode: '', busy: false, values: {}, question: '' };
      if (current.source !== 'reviewed') {
        const declared = p.immutablesControls;
        const status = current.source === 'declared' && typeof declared.getState === 'function' ? declared.getState() || {} : {};
        return { ready: p.width > 1 || (p.width === undefined && current.source === 'declared'),
          mode: current.actions.some(a => a.id === status.activeControl && ['text', 'navigation'].includes(a.type)) ? status.activeControl : '',
          managedMode: typeof status.activeControl === 'string', busy: !!status.busy,
          values: Object.fromEntries(current.actions.filter(a => a.type === 'toggle').map(a => [a.id, !!(status.values && status.values[a.id])])), question: '' };
      }
      const state = typeof readState === 'function' ? readState() : null;
      if (!state) return { ready: false, mode: '', busy: false, values: {}, question: '' };
      return {
        ready: Number(state.square) === 1 && p.width > 1,
        mode: state.chron && state.chron.on ? 'chronicle' : state.chat && state.chat.open ? 'consult' : '',
        busy: !!((state.chat && state.chat.thinking) || (state.chron && state.chron.busy)),
        question: (state.chat && state.chat.text) || '',
        values: Object.fromEntries(profile.actions.filter(a => a.type === 'toggle')
          .map(a => [a.id, a.inverted ? !state[a.field] : !!state[a.field]])),
      };
    },
    action(id) {
      const current = controls.profile();
      if (!current) return false;
      const action = [...current.actions, ...current.exports].find(a => a.id === id);
      if (!action || !controls.snapshot().ready || controls.snapshot().busy) return false;
      close();
      if (current.source === 'declared' && (!action.key || ['text', 'navigation'].includes(action.type))) {
        p.immutablesControls.onAction(id, ['text', 'navigation'].includes(action.type) ? { action: 'open' }
          : action.type === 'toggle' ? !controls.snapshot().values[id] : undefined);
      } else key(action.key);
      // Firmament's layers can also be adjusted while its animation is still.
      if (action.type !== 'export' && p.redraw) p.redraw();
      return true;
    },
    close,
    key,
    input(id, event) {
      const current = controls.profile();
      if (current && current.source === 'declared' && current.actions.some(a => a.id === id && ['text', 'navigation'].includes(a.type))) {
        p.immutablesControls.onAction(id, event);
        if (p.redraw) p.redraw();
      }
    },
    question(value) {
      const status = controls.snapshot();
      if (status.mode !== 'consult' || status.busy) return;
      const redraw = p.redraw;
      p.redraw = () => {};
      try {
        for (let i = 0; i < 60; i++) key('Backspace');
        for (const ch of pillarQuestion(value)) key(ch);
      } finally { p.redraw = redraw; p.redraw(); }
    },
  };
  p.artworkControls = controls;
  const remove = p.remove ? p.remove.bind(p) : () => {};
  p.remove = () => {
    for (const { type, wrapped, options } of listeners) window.removeEventListener(type, wrapped, options);
    p.artworkControls = null;
    remove();
  };
}
